import type { BuyerRow, ConversationStatus } from "../data/types";
import type { PipelineFilters } from "../data/store";

export function dollars(cents: number): string {
  return `$${(cents / 100).toFixed(0)}`;
}

export function initials(name: string): string {
  return name
    .split(/\s+/)
    .map((p) => p[0]?.toUpperCase() ?? "")
    .join("")
    .slice(0, 2) || "?";
}

export function applyAccountFilter(rows: BuyerRow[], selectedAccountId: "all" | string): BuyerRow[] {
  if (selectedAccountId === "all") return rows;
  return rows.filter((b) => b.account_id === selectedAccountId);
}

/** Active = not archived. Used for default views (Buyers, Closed, etc.). */
export function activeBuyers(rows: BuyerRow[]): BuyerRow[] {
  return rows.filter((b) => b.conv_archived_at == null);
}

/** Archived = the Archived view in mobile + the Archived filter on desktop. */
export function archivedBuyers(rows: BuyerRow[]): BuyerRow[] {
  return rows.filter((b) => b.conv_archived_at != null);
}

export function applyPipelineFilters(rows: BuyerRow[], f: PipelineFilters): BuyerRow[] {
  let out = rows;
  if (f.search.trim()) {
    const q = f.search.trim().toLowerCase();
    out = out.filter((b) =>
      b.display_name.toLowerCase().includes(q)
      || (b.listing_title ?? "").toLowerCase().includes(q)
    );
  }
  if (f.statuses.length > 0) {
    const allowed = new Set<ConversationStatus>(f.statuses);
    out = out.filter((b) => b.conv_status != null && allowed.has(b.conv_status));
  }
  if (f.unreadOnly) {
    out = out.filter((b) => b.conv_unread_count > 0);
  }
  return out;
}

export function filtersActive(f: PipelineFilters): boolean {
  return f.search.trim().length > 0 || f.statuses.length > 0 || f.unreadOnly;
}

export interface ClosedStats {
  totalCents:        number;
  totalTickets:      number;
  dealCount:         number;
  avgPerTicketCents: number | null;
}

export function unreadTotal(buyers: BuyerRow[]): number {
  let n = 0;
  for (const b of buyers) n += b.conv_unread_count;
  return n;
}

export function computeClosedStats(rows: BuyerRow[]): ClosedStats {
  let totalCents   = 0;
  let totalTickets = 0;
  let dealCount    = 0;
  for (const b of rows) {
    dealCount += 1;
    if (b.conv_agreed_price_cents != null) totalCents   += b.conv_agreed_price_cents;
    if (b.conv_tickets_count      != null) totalTickets += b.conv_tickets_count;
  }
  return {
    totalCents,
    totalTickets,
    dealCount,
    avgPerTicketCents: totalTickets > 0 ? Math.round(totalCents / totalTickets) : null,
  };
}

/* ============================================================
   Cross-account dedupe (FEST-PHASE-2.T)
   ============================================================
   Group rows by (E.164 phone) when present, else by normalized
   display_name. Phone matches override name matches — a buyer
   without a phone whose name happens to collide with a
   phone-bearing buyer joins the phone-matched cluster. Within
   each cluster we keep the row with the most-recent
   conv_last_activity_at as the primary; the rest are dups. */

export interface DedupeGroup {
  primary: BuyerRow;
  dups:    BuyerRow[];
  /** Why these were grouped — "phone" is high-confidence,
   *  "name" is lower (common names collide). */
  matchedBy: "phone" | "name";
}

export interface DedupeResult {
  unique:  BuyerRow[];                 // primaries only — what to actually send to
  groups:  DedupeGroup[];              // every group with > 1 member
  totalIn: number;
  totalOut: number;                    // === unique.length
  duplicateCount: number;              // === totalIn - totalOut
}

function normalizeName(s: string): string {
  return s.trim().toLowerCase().replace(/\s+/g, " ");
}

export function dedupeForBulk(rows: BuyerRow[]): DedupeResult {
  // Pass 1: cluster by phone (when present + non-empty).
  const byPhone = new Map<string, BuyerRow[]>();
  const phoneless: BuyerRow[] = [];
  for (const r of rows) {
    const phone = (r.phone ?? "").trim();
    if (phone) {
      const cur = byPhone.get(phone) ?? [];
      cur.push(r);
      byPhone.set(phone, cur);
    } else {
      phoneless.push(r);
    }
  }
  // Pass 2: cluster phoneless rows by normalized display_name.
  const byName = new Map<string, BuyerRow[]>();
  for (const r of phoneless) {
    const n = normalizeName(r.display_name);
    if (!n) continue;
    const cur = byName.get(n) ?? [];
    cur.push(r);
    byName.set(n, cur);
  }

  const groups: DedupeGroup[] = [];
  const unique: BuyerRow[] = [];

  function pickPrimary(cluster: BuyerRow[]): BuyerRow {
    return cluster.reduce((best, cur) =>
      (cur.conv_last_activity_at ?? 0) > (best.conv_last_activity_at ?? 0) ? cur : best
    );
  }

  for (const cluster of byPhone.values()) {
    const primary = pickPrimary(cluster);
    unique.push(primary);
    if (cluster.length > 1) {
      groups.push({
        primary,
        dups: cluster.filter((r) => r !== primary),
        matchedBy: "phone",
      });
    }
  }
  for (const cluster of byName.values()) {
    const primary = pickPrimary(cluster);
    unique.push(primary);
    if (cluster.length > 1) {
      groups.push({
        primary,
        dups: cluster.filter((r) => r !== primary),
        matchedBy: "name",
      });
    }
  }

  return {
    unique,
    groups,
    totalIn: rows.length,
    totalOut: unique.length,
    duplicateCount: rows.length - unique.length,
  };
}
